import { Inject, Injectable, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, defer, from, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { SearchableItem, SynonymMap } from './models/searchable-item.model';
import { SearchProvider } from './models/search-provider.model';
import { SearchResult } from './models/search-result.model';
import {
  ScoredMatch,
  SearchStrategy,
  StrategyContext,
  StrategyId,
} from './models/search-strategy.model';
import { SearchScope } from './models/search-context.model';
import {
  SearchEngineConfig,
} from './models/search-engine-config.model';

import { SEARCH_ENGINE_CONFIG } from './tokens/search-engine-config.token';
import { SEARCH_STRATEGIES } from './tokens/search-strategy.token';

import { SearchRegistryService } from './search-registry.service';
import { SearchDataLoaderService } from './search-data-loader.service';
import { SearchActionDispatcherService } from './search-action-dispatcher.service';

import { preprocessQuery } from './pipeline/query-preprocessor';
import { extractIntent } from './pipeline/intent-extractor';
import { expandSemantic } from './pipeline/semantic-expander';
import { expandCompoundParts } from './pipeline/compound-splitter';
import { tokenize } from './utils/fuzzy-search.utils';
import { normalizeDiacritics } from './utils/diacritics.util';
import { highlightMatches } from './utils/highlight.util';
import { TranslationCache } from './utils/translation-cache';

/**
 * The generic, scope-bound search engine.
 *
 * Consumers MUST pass providerIds whitelist — there is no "search all". This
 * is the structural guarantee that Settings can never accidentally surface
 * Permissions hits and vice versa.
 */
@Injectable({ providedIn: 'root' })
export class IntelligentSearchService {
  private translationCache: TranslationCache;
  /** Cache of normalized indexes per (providerId|lang) — invalidated on lang change. */
  private indexCache = new Map<string, Promise<SearchableItem<unknown>[]>>();

  constructor(
    private registry: SearchRegistryService,
    private loader: SearchDataLoaderService,
    private dispatcher: SearchActionDispatcherService,
    private translate: TranslateService,
    @Inject(SEARCH_ENGINE_CONFIG) private config: SearchEngineConfig,
    @Optional() @Inject(SEARCH_STRATEGIES) private strategies: readonly SearchStrategy[] | null
  ) {
    this.translationCache = new TranslationCache(this.translate);
    this.translate.onLangChange.subscribe(() => {
      this.indexCache.clear();
      this.translationCache.invalidate();
      this.loader.invalidate(
        (k) => k.endsWith('.de') || k.endsWith('.en')
      );
    });
  }

  /**
   * Reactive scoped search. Emits debounced results.
   * Builds a fresh observable per call so the consumer's input stream can
   * pipe directly into it via switchMap.
   */
  search(
    query: string,
    scope: SearchScope
  ): Observable<SearchResult[]> {
    return of(query).pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((q) => from(this.runSearch(q, scope)))
    );
  }

  /** Promise-based one-shot search (useful for tests and synchronous flows). */
  async searchOnce(
    query: string,
    scope: SearchScope
  ): Promise<SearchResult[]> {
    return this.runSearch(query, scope);
  }

  /** Execute the action attached to a search result. */
  async activate(result: SearchResult): Promise<void> {
    return this.dispatcher.dispatch(result.action);
  }

  /** Force-rebuild a provider's index (e.g. after backend data changed). */
  async refreshProvider(providerId: string): Promise<void> {
    const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'de';
    this.indexCache.delete(`${providerId}|${lang}`);
  }

  // ---- internal -------------------------------------------------------------

  private async runSearch(query: string, scope: SearchScope): Promise<SearchResult[]> {
    if (!query || query.trim().length < this.config.minQueryLength) return [];
    if (!scope.providerIds || scope.providerIds.length === 0) return [];

    const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'de';
    const providers = this.registry.getProviders(scope.providerIds);
    if (providers.length === 0) return [];

    const [typos, synonyms, patterns, dict] = await Promise.all([
      this.loader.loadBaseTypos(lang),
      this.loader.loadBaseSynonyms(lang),
      this.loader.loadQuestionPatterns(),
      this.loader.loadCommonWords(lang),
    ]);

    const rawQuery = query.trim();
    const normalizedQuery = preprocessQuery(rawQuery, typos, lang);
    const tokens = tokenize(normalizedQuery);
    const intent = extractIntent(rawQuery, patterns);

    // Merge base synonyms with each provider's domain synonyms.
    const providerSynonyms = await Promise.all(
      providers.map(async (p) =>
        p.getDomainSynonyms ? p.getDomainSynonyms(lang).catch(() => ({} as SynonymMap)) : ({} as SynonymMap)
      )
    );
    const mergedSynonyms: SynonymMap = { ...synonyms };
    for (const ds of providerSynonyms) for (const [k, v] of Object.entries(ds)) mergedSynonyms[k] = v;
    const expandedTerms = expandSemantic(tokens, mergedSynonyms);
    const compoundParts = dict.size > 0 ? expandCompoundParts(tokens, dict) : [];

    const ctx: StrategyContext = {
      rawQuery,
      normalizedQuery,
      tokens,
      expandedTerms,
      intent,
      compoundParts,
      language: lang,
    };

    // Gather + score per provider
    const all: SearchResult[] = [];
    for (const provider of providers) {
      const items = await this.getOrLoadIndex(provider, lang);
      const providerStrategyIds: ReadonlySet<StrategyId> | null = provider.enabledStrategies
        ? new Set(provider.enabledStrategies)
        : null;

      for (const item of items) {
        let total = 0;
        const matches: ScoredMatch[] = [];
        const activeStrategies = this.strategies ?? [];
        for (const strat of activeStrategies) {
          if (!this.config.enabledStrategies.includes(strat.id)) continue;
          if (providerStrategyIds && !providerStrategyIds.has(strat.id)) continue;
          const match = strat.score(item, ctx);
          if (match) {
            const weight = this.weightFor(strat, provider);
            const adjusted = (match.score / strat.defaultWeight) * weight;
            matches.push({ ...match, score: adjusted });
            total += adjusted;
          }
        }
        if (total > 0) {
          all.push(this.buildResult(item, provider, total, matches, ctx));
        }
      }
    }

    all.sort((a, b) => b.totalScore - a.totalScore);
    return all.slice(0, this.config.maxResults);
  }

  private weightFor(strat: SearchStrategy, provider: SearchProvider<unknown>): number {
    const override = provider.strategyWeights?.[strat.id];
    if (typeof override === 'number') return override;
    const globalOverride = this.config.weightOverrides?.[strat.id];
    if (typeof globalOverride === 'number') return globalOverride;
    return strat.defaultWeight;
  }

  private async getOrLoadIndex(
    provider: SearchProvider<unknown>,
    lang: string
  ): Promise<SearchableItem<unknown>[]> {
    const key = `${provider.id}|${lang}`;
    if (this.indexCache.has(key)) return this.indexCache.get(key)!;
    const promise = provider.getItems(lang).then((items) =>
      items.map((item) => this.normalizeItem(item, lang))
    );
    this.indexCache.set(key, promise);
    return promise;
  }

  private normalizeItem(
    item: SearchableItem<unknown>,
    lang: string
  ): SearchableItem<unknown> {
    if (item._normalizedTitle && item._normalizedKeywords) return item;
    const resolvedTitle = this.translationCache.resolve(item.title);
    const resolvedDesc = this.translationCache.resolve(item.description);
    return {
      ...item,
      _normalizedTitle: normalizeDiacritics(resolvedTitle, lang),
      _normalizedDescription: normalizeDiacritics(resolvedDesc, lang),
      _normalizedKeywords: item.keywords.map((k) =>
        normalizeDiacritics(k.toLowerCase(), lang)
      ),
    };
  }

  private buildResult(
    item: SearchableItem<unknown>,
    provider: SearchProvider<unknown>,
    totalScore: number,
    matches: ScoredMatch[],
    ctx: StrategyContext
  ): SearchResult<unknown> {
    const translatedTitle = this.translationCache.resolve(item.title);
    const translatedDescription = this.translationCache.resolve(item.description);
    const translatedBreadcrumb = item.breadcrumb.map((k) =>
      this.translationCache.resolve(k)
    );
    const highlightTerms = [
      ctx.rawQuery,
      ctx.normalizedQuery,
      ...ctx.tokens,
      ...ctx.compoundParts,
    ].filter(Boolean);
    return {
      item,
      providerId: provider.id,
      totalScore,
      matches,
      translatedTitle,
      translatedDescription,
      translatedBreadcrumb,
      highlightedTitle: highlightMatches(translatedTitle, highlightTerms),
      highlightedDescription: highlightMatches(translatedDescription, highlightTerms),
      action: provider.getActionFor(item),
    };
  }
}
